/**
 * Template Version History
 * 
 * Displays version history for a template with navigation
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CheckCircle2, FileEdit, Archive, ExternalLink } from 'lucide-react';
import type { TemplateVersion } from '@/hooks/useInspectionTemplateEditor';

interface TemplateVersionHistoryProps {
  versions: TemplateVersion[];
  currentVersionId: string;
  onSelectVersion: (id: string) => void;
}

export function TemplateVersionHistory({
  versions,
  currentVersionId,
  onSelectVersion,
}: TemplateVersionHistoryProps) {
  if (versions.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No version history available
      </div>
    );
  }

  const getStatusIcon = (version: TemplateVersion) => {
    if (version.status === 'draft') {
      return <FileEdit className="h-4 w-4 text-amber-600" />;
    }
    if (version.status === 'archived') {
      return <Archive className="h-4 w-4 text-muted-foreground" />;
    }
    if (version.is_active) {
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    }
    return null;
  };

  const getStatusBadge = (version: TemplateVersion) => {
    if (version.status === 'draft') {
      return <Badge variant="outline" className="text-amber-600 border-amber-600">Draft</Badge>;
    }
    if (version.status === 'archived') {
      return <Badge variant="secondary">Archived</Badge>;
    }
    if (version.is_active) {
      return <Badge variant="default" className="bg-green-600">Active</Badge>;
    }
    return <Badge variant="secondary">Published</Badge>;
  };

  return (
    <div className="space-y-4 mt-4">
      {versions.map((version) => {
        const isCurrent = version.id === currentVersionId;
        
        return (
          <div
            key={version.id}
            className={cn(
              'p-3 rounded-lg border transition-colors',
              isCurrent ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent'
            )}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                {getStatusIcon(version)}
                <span className="font-medium">Version {version.version}</span>
                {getStatusBadge(version)}
                {isCurrent && (
                  <Badge variant="outline" className="text-xs">Current</Badge>
                )}
              </div>
              {!isCurrent && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSelectVersion(version.id)}
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  View
                </Button>
              )}
            </div>
            
            {version.change_notes && (
              <p className="text-sm text-muted-foreground mt-2 pl-6">
                {version.change_notes}
              </p>
            )}
            
            <div className="text-xs text-muted-foreground mt-2 pl-6">
              {version.published_at ? (
                <>Published {new Date(version.published_at).toLocaleDateString()}</>
              ) : (
                <>Created {new Date(version.created_at).toLocaleDateString()}</>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
